import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button, Card, PillGroup, StatusPill } from '@/components/ui';

import { StepIndicator } from './components';
import {
  StepCustomer,
  StepShop,
  StepDesign,
  StepCollection,
  StepReview,
} from './steps';
import { STEPS, ONBOARDING_BASE_PATH } from './constants';
import { useWizardState } from './hooks/useWizardState';
import { normalizeSubdomain } from './utils';
import { completeWizard, type WizardCompleteResponse } from '@/api/onboarding';
import { OnboardingResultPage } from './OnboardingResultPage';

const RESULT_STORAGE_KEY = 'onboarding-result';

function saveOnboardingResult(state: {
  response?: WizardCompleteResponse;
  error?: string;
}): void {
  try {
    sessionStorage.setItem(RESULT_STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    console.warn('Failed to store onboarding result:', error);
  }
}

/**
 * Wizard content: the step bar, the current step on a section card and the
 * back/next footer. Renders inside the admin shell, so it brings no header
 * of its own.
 */
function WizardContent() {
  const navigate = useNavigate();
  const {
    data,
    updateData,
    currentStep,
    goToStep,
    goToNextStep,
    goToPreviousStep,
    isCurrentStepValid,
    canAccessStep,
    isStepCompleted,
    progress,
    isLastStep,
    isSubmitting,
    setIsSubmitting,
  } = useWizardState();

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      const normalizedSubdomain = normalizeSubdomain(data.subdomain);

      const payload = {
        customerEmail: data.customerEmail,
        customerName: data.customerName,
        notes: data.notes || undefined,
        shopName: data.shopName,
        language: data.language,
        subdomain: normalizedSubdomain,
        collectionId: data.collectionId,
        frontDesign: data.frontDesign
          ? {
              assetId: data.frontDesign.assetId,
              placement: data.frontPlacement,
            }
          : undefined,
        backDesign: data.backDesign
          ? {
              assetId: data.backDesign.assetId,
              placement: data.backPlacement,
            }
          : undefined,
      };

      const result = await completeWizard(payload);
      saveOnboardingResult({ response: result });
      navigate(`${ONBOARDING_BASE_PATH}/result`, { replace: true });
    } catch (error) {
      console.error('Submit error:', error);
      const message =
        error instanceof Error
          ? error.message
          : 'An error occurred. Please try again.';
      saveOnboardingResult({ error: message });
      navigate(`${ONBOARDING_BASE_PATH}/result`, { replace: true });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = () => {
    if (isLastStep) {
      void handleSubmit();
      return;
    }
    goToNextStep();
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return <StepCustomer data={data} onChange={updateData} />;
      case 1:
        return <StepShop data={data} onChange={updateData} />;
      case 2:
        return <StepCollection data={data} onChange={updateData} />;
      case 3:
        return <StepDesign data={data} onChange={updateData} />;
      case 4:
        return <StepReview data={data} />;
      default:
        return null;
    }
  };

  // `getStepIndexByPath` always yields a valid index; the fallback only
  // satisfies `noUncheckedIndexedAccess`.
  const step = STEPS[currentStep] ?? { title: '', subtitle: '' };

  return (
    <div className="mx-auto w-full max-w-5xl space-y-6">
      <nav aria-label="Setup steps">
        <PillGroup mode="nav">
          {STEPS.map((s, index) => (
            <StepIndicator
              key={s.id}
              step={s}
              index={index}
              currentStep={currentStep}
              isCompleted={isStepCompleted(index)}
              isAccessible={canAccessStep(index)}
              onClick={() => canAccessStep(index) && goToStep(index)}
            />
          ))}
        </PillGroup>
      </nav>

      <Card variant="section" padding="none">
        <div className="flex flex-wrap items-start justify-between gap-4 border-b border-line px-6 py-5">
          <div>
            <h1 className="dc-h2 text-xl text-ink">{step.title}</h1>
            <p className="mt-1 text-sm text-ink-2">{step.subtitle}</p>
          </div>
          <div className="text-right">
            <p className="mb-2 text-sm font-medium text-ink-2">
              {progress}% complete
            </p>
            <div
              role="progressbar"
              aria-label="Setup progress"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={progress}
              className="h-2 w-36 overflow-hidden rounded-pill bg-paper-3"
            >
              <div
                className="h-full rounded-pill bg-primary transition-[width] duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>

        <div className="p-6">{renderStepContent()}</div>

        <div className="flex flex-wrap items-center justify-between gap-3 rounded-b-section border-t border-line bg-paper-3 px-6 py-4">
          <div className="flex items-center gap-3">
            <StatusPill variant="neutral">
              Step {currentStep + 1} of {STEPS.length}
            </StatusPill>
            <span className="hidden text-sm text-mute sm:inline">
              You can change everything later.
            </span>
          </div>

          <div className="flex items-center gap-3">
            {currentStep > 0 && (
              <Button
                variant="outline"
                onClick={goToPreviousStep}
                disabled={isSubmitting}
              >
                <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                Back
              </Button>
            )}
            <Button
              variant="primary"
              onClick={handleNext}
              disabled={!isCurrentStepValid}
              loading={isSubmitting}
              loadingLabel="Creating..."
            >
              {isLastStep ? 'Create Shop' : 'Next'}
              {!isLastStep && (
                <ChevronRight className="h-4 w-4" aria-hidden="true" />
              )}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

/**
 * Main Onboarding Wizard Page with URL-based routing
 */
export function OnboardingWizardPage() {
  return (
    <Routes>
      {/* Default redirect to first step */}
      <Route index element={<Navigate to="customer" replace />} />

      {/* All steps use the same content component, which handles routing internally */}
      <Route path="customer" element={<WizardContent />} />
      <Route path="shop" element={<WizardContent />} />
      <Route path="design" element={<WizardContent />} />
      <Route path="collection" element={<WizardContent />} />
      <Route path="review" element={<WizardContent />} />
      <Route path="result" element={<OnboardingResultPage />} />

      {/* Fallback for invalid paths */}
      <Route path="*" element={<Navigate to="customer" replace />} />
    </Routes>
  );
}
